import React, { useEffect, useState } from 'react';
import {
  CPT_BUFFER_TIME,
  SSD_VOLUME_TABLE_HEADERS,
  MAX_ATROPS_OVERRIDE_CAPACITY_PERCENTAGE,
  MAX_ATROPS_OVERRIDE_CAPACITY_UNIT_NARROW_WINDOWS,
  SSD_FEATURE_FLAGS_LIST,
  SSD_VOLUME_SOURCES
} from '../../../../../../utils/constants';
import { chainWalk } from '../../../../../../utils/helpers';
import { isFeatureFlagEnabled } from '../../../../utils';
import { checkOverrideFeatureFlags } from '../../../../../../utils/AdminSettings';

// Sources that contribute to a planning charge post-plan-creation.
export const SSD_PLANNING_CHARGE_VOLUME_SOURCES = [
  SSD_VOLUME_SOURCES.ATROPS,
  SSD_VOLUME_SOURCES.AFT_CHARGE,
  SSD_VOLUME_SOURCES.DDP_FORECAST,
  'Override - PercentATROPS'
];

export const useUnitsOverrideUiEnabled = () => {
  const [isUnitsOverrideUiEnabled, setIsUnitsOverrideUiEnabled] = useState(false);
  useEffect(() => {
    checkOverrideFeatureFlags().then((flags) => {
      setIsUnitsOverrideUiEnabled(flags.isAtropsVolumeUnitsOverrideEnabled);
    });
  }, []);
  return isUnitsOverrideUiEnabled;
};

export const getMaxAtropsOverrideCapacityPercentage = (adminSettings) => {
  const isNarrowWindowsEnabled = isFeatureFlagEnabled(adminSettings, SSD_FEATURE_FLAGS_LIST.narrowWindowsEnabled);
  return isNarrowWindowsEnabled
    ? MAX_ATROPS_OVERRIDE_CAPACITY_UNIT_NARROW_WINDOWS
    : MAX_ATROPS_OVERRIDE_CAPACITY_PERCENTAGE;
};

const isEquivalentCpt = (demandCpt, otherCpt) => {
  // Convert timestamp strings to Date objects
  const demandTimeSeconds = Math.floor(new Date(demandCpt).getTime() / 1000);
  const otherTimeSeconds = Math.floor(new Date(otherCpt).getTime() / 1000);

  // Compare the difference in seconds
  return Math.abs(demandTimeSeconds - otherTimeSeconds) <= CPT_BUFFER_TIME;
};

/*
 * Calculates the total volume from demand data by filtering based on CPT datetime, process paths, roles, and volume sources,
 * then sums the volume values of matching entries.
 */
export const findTotalVolumeFromDemand = (demand, cptDateTimeLocal, processPaths, roles, sources) => {
  return demand
    .filter(
      ({ cpt_local, process_path, process_role, volume_source }) =>
        isEquivalentCpt(cpt_local, cptDateTimeLocal) &&
        (processPaths.length === 0 || processPaths.indexOf(process_path) >= 0) &&
        (roles.length === 0 || roles.indexOf(process_role) >= 0) &&
        (sources.length === 0 ||
          sources.filter((sourceName) => chainWalk(() => volume_source.startsWith(sourceName), false)).length > 0)
    )
    .reduce((sum, { volume }) => sum + volume, 0);
};

export const getTableHeaders = (featureFlags) => {
  const { isGrocery, isDdpEnabled } = featureFlags;
  return isGrocery
    ? [
        {
          cells: [
            { content: null },
            { content: null, width: 300 },
            { content: SSD_VOLUME_TABLE_HEADERS.PLANNING_CHARGE, backgroundColor: 'secondary' },
            { content: SSD_VOLUME_TABLE_HEADERS.RODEO_BACKLOG, backgroundColor: 'secondary' }
          ]
        }
      ]
    : [
        {
          cells: [
            { content: null },
            { content: null, width: 300 },
            { content: <strong>Sortable (Pick2Rebin)</strong>, colSpan: isDdpEnabled ? 2 : 3 }
          ]
        },
        {
          cells: [
            { content: null },
            { content: null, width: 300 },
            { content: SSD_VOLUME_TABLE_HEADERS.PREDICTED_REMAINING_CHARGE, hidden: isDdpEnabled },
            { content: SSD_VOLUME_TABLE_HEADERS.MAX_POTENTIAL_CHARGE, backgroundColor: 'secondary' },
            { content: SSD_VOLUME_TABLE_HEADERS.PLANNING_CHARGE, backgroundColor: 'secondary' },
            { content: SSD_VOLUME_TABLE_HEADERS.VNA_PREDICTED_REMAINING_CHARGE, hidden: isDdpEnabled },
            { content: SSD_VOLUME_TABLE_HEADERS.RODEO_BACKLOG },
            { content: SSD_VOLUME_TABLE_HEADERS.TOTAL_PLANNING_VOLUME, hidden: isDdpEnabled }
          ]
        }
      ];
};

export const getPredictedChargeValueByRoleName = (outboundVolume, roleName) => {
  const predictedCharge = outboundVolume?.predictedChargeList.find((item) => item.role.name?.includes(roleName));

  return predictedCharge?.chargeValue || 0;
};

export const getBacklogChargeValueByRoleName = (outboundVolume, roleName) => {
  const getPickChargeValue =
    outboundVolume?.backlogList.find((item) => {
      return item.role.process === 'pick' && item.role.name?.includes(roleName);
    })?.chargeValue || 0;

  const getPackChargeValue =
    outboundVolume?.backlogList.find((item) => {
      return item.role.process === 'pack' && item.role.name?.includes(roleName);
    })?.chargeValue || 0;

  return getPickChargeValue + getPackChargeValue;
};

export const getCapacity = (outboundVolume) => {
  return outboundVolume.capacity || 0;
};

export const getScheduled = (outboundVolume) => {
  return outboundVolume.schedule || 0;
};
